/**
 * attributionStore.ts — Centralized attribution persistence layer.
 *
 * ── DATA MODEL ────────────────────────────────────────────────────────────────
 * {
 *   // Ad click IDs
 *   fbclid:         string | null   — Facebook/Instagram click ID
 *   gclid:          string | null   — Google Ads click ID
 *   fbc:            string | null   — fb.1.{ts}.{fbclid} — Meta CAPI fbc param
 *   fbclid_ts:      string | null   — ms timestamp when fbclid was first seen
 *
 *   // UTM params
 *   utm_source:     string | null
 *   utm_medium:     string | null
 *   utm_campaign:   string | null
 *   utm_term:       string | null
 *   utm_content:    string | null
 *
 *   // Custom ref
 *   ref:            string | null   — ?ref= custom label (any string)
 *
 *   // Landing context
 *   landing_url:    string | null   — first URL the user landed on
 *   referrer:       string | null   — document.referrer on first load
 *   first_seen_at:  string | null   — ISO timestamp of first session
 *
 *   // Session identity
 *   session_id:     string          — random UUID per browser session
 *
 *   // Order context (set after order creation)
 *   confirmation_id: string | null
 *   coupon_code:     string | null
 *   selected_state:  string | null
 * }
 *
 * ── STORAGE STRATEGY ─────────────────────────────────────────────────────────
 * sessionStorage: all fields — survives SPA navigation, cleared on tab close
 * localStorage:   fbclid, fbclid_ts, fbc, first_seen_at, session_id only
 *                 — survives tab close for CAPI dedup on return visits
 *
 * ── MERGE RULES (LAST-TOUCH) ─────────────────────────────────────────────────
 * 1. Fresh URL campaign params OVERRIDE stale stored click IDs / UTMs.
 *    - URL utm_source  → clears stored gclid + fbclid (+derivs)
 *    - URL fbclid      → clears stored gclid + utm_*
 *    - URL gclid       → clears stored fbclid + utm_*
 * 2. Existing non-empty stored values are NEVER overwritten with empty/null
 * 3. fbclid is special: also persisted to localStorage for cross-session survival
 * 4. landing_url, referrer, first_seen_at: only set ONCE (first landing)
 * 5. session_id: generated once per browser session, never changes
 *
 * ── DEV LOGGING ──────────────────────────────────────────────────────────────
 * All capture/restore/merge operations log to console in DEV mode only.
 * Set localStorage.debug_attribution = "1" to enable in production.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { sanitizeMacroValue } from "./attributionMacros";
import { detectAiChannelFromReferrer } from "./aiReferral";
import { maskCheckoutSlugPath } from "./checkoutSlugMask";

const SS = typeof sessionStorage !== "undefined" ? sessionStorage : null;
const LS = typeof localStorage !== "undefined" ? localStorage : null;

const IS_DEV = import.meta.env.DEV;

function debugLog(action: string, data: Record<string, unknown>): void {
  const forceDebug = LS?.getItem("debug_attribution") === "1";
  if (!IS_DEV && !forceDebug) return;
  console.groupCollapsed(`[Attribution] ${action}`);
  console.table(data);
  console.groupEnd();
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AttributionData {
  // Ad click IDs — Meta
  fbclid: string | null;
  fbc: string | null;
  fbclid_ts: string | null;

  // Ad click IDs — Google
  gclid: string | null;
  gbraid: string | null;   // Google web-to-app (privacy-safe)
  wbraid: string | null;   // Google app-to-web (privacy-safe)

  // Ad click IDs — Microsoft Ads
  msclkid: string | null;

  // Ad click IDs — TikTok
  ttclid: string | null;

  // UTM params
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;

  // Ad campaign / keyword params (Google Ads ValueTrack + Meta dynamic).
  // These are only present when the ad final-URL suffix passes them through;
  // we capture them verbatim and NEVER invent a value. Stored session-scoped
  // alongside the UTMs so they survive SPA navigation into checkout.
  gad_source: string | null;   // Google Ads gad_source param
  keyword: string | null;      // {keyword} ValueTrack (search keyword)
  search_term: string | null;  // explicit search/query term when passed
  matchtype: string | null;    // {matchtype} — e (exact), p (phrase), b (broad)
  network: string | null;      // {network} — g (search), s (search partner), d (display)
  device: string | null;       // {device} — m / t / c
  placement: string | null;    // {placement} — display/site placement
  campaign_id: string | null;  // numeric campaign id (Google/Meta)
  adset_id: string | null;     // ad set / ad group id
  ad_id: string | null;        // ad / creative id

  // Custom ref
  ref: string | null;

  // Landing context
  landing_url: string | null;
  referrer: string | null;
  first_seen_at: string | null;

  // Session identity
  session_id: string;

  // Order context (populated after order creation)
  confirmation_id: string | null;
  coupon_code: string | null;
  selected_state: string | null;
}

// ── Storage keys ──────────────────────────────────────────────────────────────

const KEYS: Record<keyof AttributionData, string> = {
  fbclid:          "fbclid",
  fbc:             "fbc",
  fbclid_ts:       "fbclid_ts",
  gclid:           "gclid",
  gbraid:          "gbraid",
  wbraid:          "wbraid",
  msclkid:         "msclkid",
  ttclid:          "ttclid",
  utm_source:      "utm_source",
  utm_medium:      "utm_medium",
  utm_campaign:    "utm_campaign",
  utm_term:        "utm_term",
  utm_content:     "utm_content",
  gad_source:      "gad_source",
  keyword:         "kw",
  search_term:     "search_term",
  matchtype:       "matchtype",
  network:         "adnetwork",
  device:          "addevice",
  placement:       "placement",
  campaign_id:     "campaign_id",
  adset_id:        "adset_id",
  ad_id:           "ad_id",
  ref:             "esa_referred_by",
  landing_url:     "landing_url",
  referrer:        "referrer",
  first_seen_at:   "first_seen_at",
  session_id:      "session_id",
  confirmation_id: "confirmation_id",
  coupon_code:     "coupon_code",
  selected_state:  "selected_state",
};

// Fields that are also persisted to localStorage (survive tab close)
const LS_KEYS: Set<keyof AttributionData> = new Set([
  "fbclid",
  "fbc",
  "fbclid_ts",
  "msclkid",
  "ttclid",
  "gbraid",
  "wbraid",
  "first_seen_at",
  "session_id",
]);

// Fields that are only set ONCE (first landing) — never overwritten
const ONCE_KEYS: Set<keyof AttributionData> = new Set([
  "landing_url",
  "referrer",
  "first_seen_at",
  "session_id",
]);

// ── Click-ID provenance (ATTRIBUTION-SOURCE-IMMUTABILITY-001) ─────────────────
//
// Click IDs survive tab close (localStorage) so conversion evidence is never
// lost — but a RESTORED click ID is not proof that the CURRENT visit was a
// paid click. LIVE order PT-MT1GWHXX: an organic-first lead re-opened a clean,
// manually-shared provider link; stale gclid/gbraid restored from localStorage
// were stamped into the new last-touch as channel "google_ads" and from there
// into the order's flat click-ID columns, flipping the Admin source badge to
// Google Ads.
//
// Contract:
//   provenance "url"     — the ID was present in a URL captured during THIS
//                          tab session. Only this counts as a fresh paid click.
//   provenance "storage" — the ID was restored from a previous session's
//                          localStorage. It remains available for conversion
//                          evidence (getAttribution) but must NEVER mint a new
//                          paid touch or paid channel label.
//   no marker            — unknown provenance. FAIL CLOSED: treated as
//                          "storage", never as a fresh click.
//
// Markers live in sessionStorage (per tab session, like the touch they
// describe) and are written only by captureFromUrl.
export type ClickIdField = "gclid" | "gbraid" | "wbraid" | "fbclid" | "msclkid" | "ttclid";
export type ClickIdProvenance = "url" | "storage";

export const CLICK_ID_FIELDS: ClickIdField[] = [
  "gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid",
];

const PROV_PREFIX = "pt_prov_";

// Internal links deliberately carry campaign labels (UTMs/ValueTrack) but not
// raw click IDs. Without an explicit marker, captureFromUrl() mistakes those
// repeated labels for a brand-new campaign and clears the genuine gclid /
// gbraid / wbraid on the first in-site navigation.
const INTERNAL_ATTR_PARAM = "pt_internal_attr";

function provKey(field: ClickIdField): string {
  return PROV_PREFIX + KEYS[field];
}

function setProvenance(field: ClickIdField, source: ClickIdProvenance): void {
  // "url" always wins for the tab session; "storage" never downgrades it.
  const existing = ssGet(provKey(field));
  if (source === "storage" && existing === "url") return;
  ssSet(provKey(field), source);
}

function clearProvenance(field: ClickIdField): void {
  ssDel(provKey(field));
}

/**
 * Provenance of a click ID for the CURRENT tab session.
 * Returns null when the ID is absent entirely. A present ID with no marker
 * (legacy tab, storage written outside captureFromUrl) fails closed to
 * "storage" — never to "url".
 */
export function getClickIdProvenance(field: ClickIdField): ClickIdProvenance | null {
  const marker = ssGet(provKey(field));
  if (marker === "url" || marker === "storage") return marker;
  const present = ssGet(KEYS[field]) || lsGet(KEYS[field]);
  return present ? "storage" : null;
}

/** Full provenance map, embedded into touch snapshots for downstream audits. */
export function getClickIdProvenanceMap(): Record<ClickIdField, ClickIdProvenance | null> {
  const map = {} as Record<ClickIdField, ClickIdProvenance | null>;
  CLICK_ID_FIELDS.forEach((f) => { map[f] = getClickIdProvenance(f); });
  return map;
}

/**
 * Query params that are CREDENTIALS, not attribution signal.
 *
 * ORDER-RESUME-SECURE-TOKEN-AND-PII-CONFIDENTIALITY-001 §J
 * `rt` carries a live resume token. Attribution capture runs on mount — BEFORE
 * the assessment page scrubs the URL — so without this strip the raw token was
 * persisted verbatim into sessionStorage `landing_url`, and from there into the
 * order row, the GHL payload and every analytics event carrying landing_url.
 * Found by inspecting real browser storage; source review alone missed it.
 *
 * `resume` is stripped too: a confirmation id is a display reference that does
 * not belong in an attribution record either.
 */
const CREDENTIAL_PARAMS = ["rt", "resume", "token"];

/**
 * Remove credential-bearing params from a URL, preserving everything else.
 *
 * ASSESSMENT-CHECKOUT-REFRESH-AND-RESUME-PERSISTENCE-LIVE-INCIDENT-003:
 * credentials are no longer only in the QUERY. The durable payment URL is
 * `/checkout/<slug>` and that slug is a credential in the PATH, so a
 * query-only strip would have let it through into `landing_url` — which is
 * written to the order row and forwarded to GHL and analytics. The path mask
 * runs on every call, including the ones that strip no params at all.
 */
export function stripCredentialParams(rawUrl: string): string {
  if (!rawUrl) return rawUrl;
  try {
    const u = new URL(rawUrl, typeof window !== "undefined" ? window.location.origin : undefined);
    let touched = false;
    for (const p of CREDENTIAL_PARAMS) {
      if (u.searchParams.has(p)) { u.searchParams.delete(p); touched = true; }
    }
    return maskCheckoutSlugPath(touched ? u.toString() : rawUrl);
  } catch {
    // Unparseable URL: fail SAFE by dropping the query string entirely rather
    // than returning a string that might still embed a token.
    return maskCheckoutSlugPath(rawUrl.split("?")[0]);
  }
}

// Ad campaign / keyword params and the URL query aliases we accept for each.
// Google Ads ValueTrack ({keyword}, {matchtype}, {network}, {device},
// {placement}, {campaignid}, {adgroupid}, {creative}) and Meta dynamic
// ({{campaign.id}}, {{adset.id}}, {{ad.id}}) commonly arrive under several
// different param names depending on how the final-URL suffix was set up,
// so each canonical field matches a small alias list. First non-empty wins.
const AD_PARAM_ALIASES: Array<[keyof AttributionData, string[]]> = [
  ["gad_source",  ["gad_source", "gad"]],
  ["keyword",     ["keyword", "kw", "adgroup_keyword"]],
  ["search_term", ["search_term", "searchterm", "q_term", "matched_query"]],
  ["matchtype",   ["matchtype", "match_type"]],
  ["network",     ["network", "adnetwork", "net"]],
  ["device",      ["device", "addevice", "dev"]],
  ["placement",   ["placement", "adplacement"]],
  ["campaign_id", ["campaign_id", "campaignid", "utm_campaign_id"]],
  ["adset_id",    ["adset_id", "adsetid", "adgroupid", "ad_group_id", "adgroup_id"]],
  ["ad_id",       ["ad_id", "adid", "creative", "creativeid", "ad_name_id"]],
];

// Stale ad params from a PRIOR channel must be cleared when a fresh click ID
// from a DIFFERENT channel arrives (mirrors the existing utm_* clear logic).
const AD_PARAM_KEYS: string[] = AD_PARAM_ALIASES.map(([field]) => KEYS[field]);

// ── Helpers ───────────────────────────────────────────────────────────────────

function ssGet(key: string): string | null {
  return SS?.getItem(key) || null;
}

function ssSet(key: string, value: string): void {
  SS?.setItem(key, value);
}

function ssDel(key: string): void {
  SS?.removeItem(key);
}

function lsGet(key: string): string | null {
  return LS?.getItem(key) || null;
}

function lsSet(key: string, value: string): void {
  LS?.setItem(key, value);
}

function lsDel(key: string): void {
  LS?.removeItem(key);
}

function clearBoth(key: string): boolean {
  const had = !!(ssGet(key) || lsGet(key));
  if (had) {
    ssDel(key);
    lsDel(key);
  }
  return had;
}

function generateSessionId(): string {
  // crypto.randomUUID() is available in all modern browsers
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Normalize a raw utm_source into a canonical channel string.
 * facebook / fb / instagram / ig / meta → "facebook_ads"
 * google                                  → "google_ads"
 * anything else                           → lowercased trimmed value
 */
function normalizeSource(source: string): string {
  const s = (source || "").toLowerCase().trim();
  if (!s) return "";
  if (s === "facebook" || s === "fb" || s === "instagram" || s === "ig" || s === "meta") {
    return "facebook_ads";
  }
  if (s === "google") return "google_ads";
  return s;
}

// ── Core: capture from URL params ────────────────────────────────────────────

/**
 * Called on every route change by UTMCapture in App.tsx.
 *
 * LAST-TOUCH priority: a fresh campaign touch in the URL OVERRIDES the
 * previously stored attribution from a different channel. Click IDs still
 * get stored, but stale ones from the prior touch are cleared out so
 * buildChannel / buildAttributionJson reflect the newest campaign source.
 */
export function captureFromUrl(search: string): void {
  const params = new URLSearchParams(search);
  const now = Date.now();
  const captured: Record<string, string> = {};
  const restored: Record<string, string> = {};
  const skipped: Record<string, string> = {};
  const cleared: string[] = [];

  // utm_source is sanitized so an UN-EXPANDED macro (e.g. "{{site_source_name}}")
  // is treated as absent — it must NOT be stored AND must NOT trigger the
  // last-touch override below that would clear a real gclid/fbclid.
  const urlUtmSource = sanitizeMacroValue(params.get("utm_source"));
  const urlFbclid    = params.get("fbclid");
  const urlGclid     = params.get("gclid");
  // Some same-origin hard links carry campaign labels without our marker.
  // Preserve only a URL-proven ID in this tab and only for the same Google
  // campaign. External/new-campaign visits and restored storage still reset.
  let sameOriginReferrer = false;
  try { sameOriginReferrer = !!document.referrer && new URL(document.referrer).origin === window.location.origin; } catch { /* no trusted referrer */ }
  const medium = (sanitizeMacroValue(params.get("utm_medium")) ?? "").toLowerCase();
  const source = (urlUtmSource ?? "").toLowerCase();
  const campaign = sanitizeMacroValue(params.get("utm_campaign"));
  const originalTouch = getFirstTouch();
  const storedCampaign = originalTouch?.utm_campaign;
  const sameGoogleCampaign = source === "google" && ["cpc", "ppc", "paid", "paid_search", "search_ad"].includes(medium)
    && !!campaign && campaign === storedCampaign
    && (originalTouch?.utm_source ?? "").toLowerCase() === "google";
  const hasProvenGoogleClick = (["gclid", "gbraid", "wbraid"] as ClickIdField[])
    .some((field) => !!ssGet(KEYS[field]) && getClickIdProvenance(field) === "url"
      && originalTouch?.[field] === ssGet(KEYS[field]));
  const hasIncomingClick = CLICK_ID_FIELDS.some((field) => !!params.get(field));
  const isInternalCarry = params.get(INTERNAL_ATTR_PARAM) === "1"
    || (sameOriginReferrer && sameGoogleCampaign && hasProvenGoogleClick && !hasIncomingClick);

  // ── 1. Session ID — generate once, never change ───────────────────────────
  const existingSessionId = ssGet(KEYS.session_id) || lsGet(KEYS.session_id);
  if (!existingSessionId) {
    const newId = generateSessionId();
    ssSet(KEYS.session_id, newId);
    lsSet(KEYS.session_id, newId);
    captured.session_id = newId;
  }

  // ── 2. first_seen_at — set once, never overwrite ──────────────────────────
  if (!ssGet(KEYS.first_seen_at) && !lsGet(KEYS.first_seen_at)) {
    const ts = new Date(now).toISOString();
    ssSet(KEYS.first_seen_at, ts);
    lsSet(KEYS.first_seen_at, ts);
    captured.first_seen_at = ts;
  } else if (!ssGet(KEYS.first_seen_at)) {
    // Restore from localStorage (returning visitor)
    const stored = lsGet(KEYS.first_seen_at)!;
    ssSet(KEYS.first_seen_at, stored);
    restored.first_seen_at = stored;
  }

  // ── 2b. LAST-TOUCH override: clear stale attribution from OTHER channels ──
  //        Only runs when this URL itself carries a fresh campaign signal.
  if (urlUtmSource && !isInternalCarry) {
    // Fresh utm_source wins over any previously stored click IDs.
    [KEYS.gclid, KEYS.fbclid, KEYS.fbc, KEYS.fbclid_ts, KEYS.gbraid, KEYS.wbraid].forEach((k) => {
      if (clearBoth(k)) cleared.push(k);
    });
    (["gclid", "fbclid", "gbraid", "wbraid"] as ClickIdField[]).forEach(clearProvenance);
  } else if (urlFbclid) {
    // Fresh fbclid wins over stale gclid and stale utm_* / ad params.
    [KEYS.gclid, KEYS.gbraid, KEYS.wbraid,
     KEYS.utm_source, KEYS.utm_medium, KEYS.utm_campaign, KEYS.utm_term, KEYS.utm_content,
     ...AD_PARAM_KEYS].forEach((k) => {
      if (clearBoth(k)) cleared.push(k);
    });
    (["gclid", "gbraid", "wbraid"] as ClickIdField[]).forEach(clearProvenance);
    ssDel("utm_captured");
  } else if (urlGclid) {
    // Fresh gclid wins over stale fbclid and stale utm_* / ad params.
    [KEYS.fbclid, KEYS.fbc, KEYS.fbclid_ts,
     KEYS.utm_source, KEYS.utm_medium, KEYS.utm_campaign, KEYS.utm_term, KEYS.utm_content,
     ...AD_PARAM_KEYS].forEach((k) => {
      if (clearBoth(k)) cleared.push(k);
    });
    clearProvenance("fbclid");
    ssDel("utm_captured");
  }

  // ── 3. fbclid — ALWAYS capture from URL; restore from LS if missing ───────
  if (urlFbclid) {
    ssSet(KEYS.fbclid, urlFbclid);
    lsSet(KEYS.fbclid, urlFbclid);
    const fbclidTs = String(now);
    ssSet(KEYS.fbclid_ts, fbclidTs);
    lsSet(KEYS.fbclid_ts, fbclidTs);
    const fbc = `fb.1.${fbclidTs}.${urlFbclid}`;
    ssSet(KEYS.fbc, fbc);
    lsSet(KEYS.fbc, fbc);
    setProvenance("fbclid", "url");
    captured.fbclid = urlFbclid;
    captured.fbc = fbc;
  } else {
    // Restore fbclid from localStorage if not in sessionStorage
    const storedFbclid = ssGet(KEYS.fbclid) || lsGet(KEYS.fbclid);
    if (storedFbclid && !ssGet(KEYS.fbclid)) {
      ssSet(KEYS.fbclid, storedFbclid);
      const storedTs = lsGet(KEYS.fbclid_ts) || String(now);
      ssSet(KEYS.fbclid_ts, storedTs);
      const storedFbc = lsGet(KEYS.fbc) || `fb.1.${storedTs}.${storedFbclid}`;
      ssSet(KEYS.fbc, storedFbc);
      setProvenance("fbclid", "storage");
      restored.fbclid = storedFbclid;
      restored.fbc = storedFbc;
    }
  }

  // ── 4. gclid / gbraid / wbraid — Google click IDs ────────────────────────
  if (urlGclid) {
    ssSet(KEYS.gclid, urlGclid);
    lsSet(KEYS.gclid, urlGclid);
    setProvenance("gclid", "url");
    captured.gclid = urlGclid;
  } else if (!ssGet(KEYS.gclid)) {
    const stored = lsGet(KEYS.gclid);
    if (stored) { ssSet(KEYS.gclid, stored); setProvenance("gclid", "storage"); restored.gclid = stored; }
  }

  const gbraidFromUrl = params.get("gbraid");
  if (gbraidFromUrl) {
    ssSet(KEYS.gbraid, gbraidFromUrl);
    lsSet(KEYS.gbraid, gbraidFromUrl);
    setProvenance("gbraid", "url");
    captured.gbraid = gbraidFromUrl;
  } else if (!ssGet(KEYS.gbraid)) {
    const stored = lsGet(KEYS.gbraid);
    if (stored) { ssSet(KEYS.gbraid, stored); setProvenance("gbraid", "storage"); restored.gbraid = stored; }
  }

  const wbraidFromUrl = params.get("wbraid");
  if (wbraidFromUrl) {
    ssSet(KEYS.wbraid, wbraidFromUrl);
    lsSet(KEYS.wbraid, wbraidFromUrl);
    setProvenance("wbraid", "url");
    captured.wbraid = wbraidFromUrl;
  } else if (!ssGet(KEYS.wbraid)) {
    const stored = lsGet(KEYS.wbraid);
    if (stored) { ssSet(KEYS.wbraid, stored); setProvenance("wbraid", "storage"); restored.wbraid = stored; }
  }

  // ── 4b. msclkid — Microsoft Ads click ID ─────────────────────────────────
  const msclkidFromUrl = params.get("msclkid");
  if (msclkidFromUrl) {
    ssSet(KEYS.msclkid, msclkidFromUrl);
    lsSet(KEYS.msclkid, msclkidFromUrl);
    setProvenance("msclkid", "url");
    captured.msclkid = msclkidFromUrl;
  } else if (!ssGet(KEYS.msclkid)) {
    const stored = lsGet(KEYS.msclkid);
    if (stored) { ssSet(KEYS.msclkid, stored); setProvenance("msclkid", "storage"); restored.msclkid = stored; }
  }

  // ── 4c. ttclid — TikTok click ID ─────────────────────────────────────────
  const ttclidFromUrl = params.get("ttclid");
  if (ttclidFromUrl) {
    ssSet(KEYS.ttclid, ttclidFromUrl);
    lsSet(KEYS.ttclid, ttclidFromUrl);
    setProvenance("ttclid", "url");
    captured.ttclid = ttclidFromUrl;
  } else if (!ssGet(KEYS.ttclid)) {
    const stored = lsGet(KEYS.ttclid);
    if (stored) { ssSet(KEYS.ttclid, stored); setProvenance("ttclid", "storage"); restored.ttclid = stored; }
  }

  // ── 5. UTMs + ref ────────────────────────────────────────────────────────
  const utmFields: Array<[keyof AttributionData, string]> = [
    ["utm_source",   "utm_source"],
    ["utm_medium",   "utm_medium"],
    ["utm_campaign", "utm_campaign"],
    ["utm_term",     "utm_term"],
    ["utm_content",  "utm_content"],
  ];

  if (urlUtmSource && !isInternalCarry) {
    // LAST-TOUCH: fresh utm_source in URL → OVERRIDE any previously stored UTMs.
    utmFields.forEach(([field, param]) => {
      const val = sanitizeMacroValue(params.get(param));
      if (val) {
        const existing = ssGet(KEYS[field]);
        if (existing && existing !== val) {
          skipped[`${field}_overwrote`] = `replaced "${existing}" with "${val}"`;
        }
        ssSet(KEYS[field], val);
        captured[field] = val;
      }
    });
    // ref is still set-once
    const refVal = sanitizeMacroValue(params.get("ref"));
    if (refVal && !ssGet(KEYS.ref)) {
      ssSet(KEYS.ref, refVal);
      captured.ref = refVal;
    }
    ssSet("utm_captured", "1");
  } else if (!ssGet("utm_captured")) {
    // First-landing: capture everything that's in the URL.
    utmFields.forEach(([field, param]) => {
      const val = sanitizeMacroValue(params.get(param));
      if (val) {
        ssSet(KEYS[field], val);
        captured[field] = val;
      }
    });
    const refVal = sanitizeMacroValue(params.get("ref"));
    if (refVal) {
      ssSet(KEYS.ref, refVal);
      captured.ref = refVal;
    }
    ssSet("utm_captured", "1");
  } else {
    // After first landing, no fresh utm_source in URL:
    // fill any still-missing UTMs (don't overwrite existing values).
    utmFields.forEach(([field, param]) => {
      const val = sanitizeMacroValue(params.get(param));
      const existing = ssGet(KEYS[field]);
      if (val && !existing) {
        ssSet(KEYS[field], val);
        captured[field] = val;
      } else if (val && existing && val !== existing) {
        skipped[field] = `kept "${existing}", ignored "${val}"`;
      }
    });
  }

  // ── 5b. Ad campaign / keyword params (Google ValueTrack + Meta dynamic) ───
  //        Captured verbatim — never invented. A fresh campaign signal in
  //        THIS url (utm_source / fbclid / gclid) allows overriding stale
  //        values; otherwise we fill-missing (set-once) so later internal
  //        navigation doesn't blank them. Mirrors the UTM precedence above.
  {
    const freshCampaign = !isInternalCarry && !!(urlUtmSource || urlFbclid || urlGclid);
    AD_PARAM_ALIASES.forEach(([field, aliases]) => {
      let val: string | null = null;
      for (const alias of aliases) {
        const v = sanitizeMacroValue(params.get(alias));
        if (v) { val = v; break; }
      }
      if (!val) return;
      const existing = ssGet(KEYS[field]);
      if (!existing) {
        ssSet(KEYS[field], val);
        captured[field] = val;
      } else if (freshCampaign && existing !== val) {
        ssSet(KEYS[field], val);
        captured[field] = val;
        skipped[`${field}_overwrote`] = `replaced "${existing}" with "${val}"`;
      }
    });
  }

  // landing_url and referrer — set once, ALWAYS credential-stripped
  if (!ssGet(KEYS.landing_url)) {
    const url = stripCredentialParams(
      typeof window !== "undefined" ? window.location.href : "",
    );
    ssSet(KEYS.landing_url, url);
    captured.landing_url = url;
  }
  if (!ssGet(KEYS.referrer)) {
    const ref = stripCredentialParams(
      typeof document !== "undefined" ? document.referrer : "",
    );
    ssSet(KEYS.referrer, ref);
    if (ref) captured.referrer = ref;
  }

  // ── Seed first-touch (set-once, never overwritten) ────────────────────────
  // Done after all captures land so the first-touch snapshot reflects the
  // very first attribution state, including click IDs and landing context.
  try {
    getOrInitFirstTouch();
  } catch {
    // ignore — never break the page
  }

  // ── Dev log ───────────────────────────────────────────────────────────────
  const hasActivity =
    Object.keys(captured).length > 0 ||
    Object.keys(restored).length > 0 ||
    Object.keys(skipped).length > 0 ||
    cleared.length > 0;
  if (hasActivity) {
    debugLog("captureFromUrl", {
      url: search || "(no params)",
      captured,
      restored,
      cleared: cleared.length ? cleared.join(", ") : "(none)",
      skipped,
    });
  }
}

// ── Core: read full attribution snapshot ─────────────────────────────────────

/**
 * Returns the full attribution data object from sessionStorage + localStorage.
 * Safe to call anywhere — never throws.
 */
export function getAttribution(): AttributionData {
  const get = (key: keyof AttributionData): string | null => {
    const ssVal = ssGet(KEYS[key]);
    if (ssVal) return ssVal;
    // Fallback to localStorage for durable fields
    if (LS_KEYS.has(key)) return lsGet(KEYS[key]);
    return null;
  };

  const sessionId = get("session_id") || generateSessionId();

  const data: AttributionData = {
    fbclid:          get("fbclid"),
    fbc:             get("fbc"),
    fbclid_ts:       get("fbclid_ts"),
    gclid:           get("gclid"),
    gbraid:          get("gbraid"),
    wbraid:          get("wbraid"),
    msclkid:         get("msclkid"),
    ttclid:          get("ttclid"),
    utm_source:      get("utm_source"),
    utm_medium:      get("utm_medium"),
    utm_campaign:    get("utm_campaign"),
    utm_term:        get("utm_term"),
    utm_content:     get("utm_content"),
    gad_source:      get("gad_source"),
    keyword:         get("keyword"),
    search_term:     get("search_term"),
    matchtype:       get("matchtype"),
    network:         get("network"),
    device:          get("device"),
    placement:       get("placement"),
    campaign_id:     get("campaign_id"),
    adset_id:        get("adset_id"),
    ad_id:           get("ad_id"),
    ref:             get("ref"),
    landing_url:     get("landing_url"),
    referrer:        get("referrer"),
    first_seen_at:   get("first_seen_at"),
    session_id:      sessionId,
    confirmation_id: get("confirmation_id"),
    coupon_code:     get("coupon_code"),
    selected_state:  get("selected_state"),
  };

  return data;
}

// ── Setters for order-context fields ─────────────────────────────────────────

/** Call after order/confirmation ID is generated */
export function setConfirmationId(id: string): void {
  if (!id) return;
  ssSet(KEYS.confirmation_id, id);
  debugLog("setConfirmationId", { confirmation_id: id });
}

/** Call when a coupon is applied */
export function setCouponCode(code: string): void {
  if (!code) return;
  ssSet(KEYS.coupon_code, code);
  debugLog("setCouponCode", { coupon_code: code });
}

/** Call when user selects a state */
export function setSelectedState(state: string): void {
  if (!state) return;
  ssSet(KEYS.selected_state, state);
  debugLog("setSelectedState", { selected_state: state });
}

// ── First-touch preservation ─────────────────────────────────────────────────
//
// The store above is LAST-TOUCH by design. We additionally snapshot the very
// FIRST attribution touch into a single localStorage key, set ONCE per browser
// and never overwritten. This snapshot becomes orders.first_touch_json.
//
// Storage key: "pt_first_touch" — independent of the per-field LS keys so a
// last-touch override (which clears stale click IDs) cannot mutate it.
const FIRST_TOUCH_KEY = "pt_first_touch";

interface FirstTouchSnapshot {
  session_id: string;
  first_seen_at: string | null;
  channel: string;
  fullSource: string;
  fbclid: string | null;
  fbc: string | null;
  fbclid_ts: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  msclkid: string | null;
  ttclid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gad_source: string | null;
  keyword: string | null;
  search_term: string | null;
  matchtype: string | null;
  network: string | null;
  device: string | null;
  placement: string | null;
  campaign_id: string | null;
  adset_id: string | null;
  ad_id: string | null;
  ref: string | null;
  landing_url: string | null;
  referrer: string | null;
  captured_at: string;
  // ATTRIBUTION-SOURCE-IMMUTABILITY-001: per-click-ID provenance for THIS
  // snapshot ("url" = captured from a URL in this tab session, "storage" =
  // restored from a previous session). Absent on legacy snapshots — readers
  // must fail closed (treat click IDs without "url" provenance as NOT a
  // fresh paid click).
  click_provenance?: Record<string, ClickIdProvenance | null>;
  provenance_version?: number;
}

/**
 * Returns the persisted first-touch snapshot, creating it once if absent.
 * Once set, it is NEVER overwritten — calling this on a returning visitor
 * yields the original first-touch data.
 *
 * Storage lives inside the same attributionStore localStorage namespace —
 * this is NOT a separate system, just an extra key alongside the per-field
 * ones. Writes are guarded so a quota error / disabled storage cannot throw.
 */
export function getOrInitFirstTouch(): FirstTouchSnapshot | null {
  // Try to read existing snapshot first — set-once semantics.
  try {
    const existing = lsGet(FIRST_TOUCH_KEY);
    if (existing) {
      try {
        const parsed = JSON.parse(existing) as FirstTouchSnapshot;
        if (parsed && parsed.session_id) return parsed;
      } catch {
        // Malformed — fall through and overwrite once.
      }
    }
  } catch {
    return null;
  }

  // Build snapshot from current attribution state.
  let snapshot: FirstTouchSnapshot;
  try {
    const data = getAttribution();
    // ATTRIBUTION-SOURCE-IMMUTABILITY-001: a first touch is MINTED from the
    // current visit, so it may only record click IDs this visit can prove
    // (provenance "url"). Without this gate a browser whose storage already
    // held an unmarked click ID — a pre-fix bundle, or the index.html inline
    // capture — minted a brand-new first touch that looked like a paid
    // acquisition, which is exactly the fabrication this task exists to stop.
    // An ALREADY-persisted first touch is returned untouched above, so a
    // genuine paid acquisition recorded earlier is never re-gated here.
    const freshFt = (field: ClickIdField, value: string | null): string | null =>
      value && getClickIdProvenance(field) === "url" ? value : null;
    const ftFbclid = freshFt("fbclid", data.fbclid);
    snapshot = {
      session_id:    data.session_id,
      first_seen_at: data.first_seen_at,
      channel:       buildChannel(),
      fullSource:    buildFullSource(),
      fbclid:        ftFbclid,
      fbc:           ftFbclid ? data.fbc : null,
      fbclid_ts:     ftFbclid ? data.fbclid_ts : null,
      gclid:         freshFt("gclid", data.gclid),
      gbraid:        freshFt("gbraid", data.gbraid),
      wbraid:        freshFt("wbraid", data.wbraid),
      msclkid:       freshFt("msclkid", data.msclkid),
      ttclid:        freshFt("ttclid", data.ttclid),
      utm_source:    data.utm_source,
      utm_medium:    data.utm_medium,
      utm_campaign:  data.utm_campaign,
      utm_term:      data.utm_term,
      utm_content:   data.utm_content,
      gad_source:    data.gad_source,
      keyword:       data.keyword,
      search_term:   data.search_term,
      matchtype:     data.matchtype,
      network:       data.network,
      device:        data.device,
      placement:     data.placement,
      campaign_id:   data.campaign_id,
      adset_id:      data.adset_id,
      ad_id:         data.ad_id,
      ref:           data.ref,
      landing_url:   data.landing_url,
      referrer:      data.referrer,
      captured_at:   new Date().toISOString(),
      click_provenance: getClickIdProvenanceMap(),
      provenance_version: 1,
    };
  } catch {
    return null;
  }

  try {
    lsSet(FIRST_TOUCH_KEY, JSON.stringify(snapshot));
  } catch {
    // localStorage unavailable / quota — fine, return the in-memory snapshot.
  }

  try { debugLog("first_touch.init", snapshot as unknown as Record<string, unknown>); } catch { /* ignore */ }
  return snapshot;
}

/** Returns the first-touch snapshot if it exists, null otherwise. Read-only. */
export function getFirstTouch(): FirstTouchSnapshot | null {
  try {
    const raw = lsGet(FIRST_TOUCH_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as FirstTouchSnapshot;
  } catch {
    return null;
  }
}

/**
 * Returns a last-touch snapshot in the same shape as first-touch. Always
 * reflects the current session state (overwritable). Never throws.
 *
 * ATTRIBUTION-SOURCE-IMMUTABILITY-001: a last-touch describes the CURRENT
 * visit. Click IDs merely restored from a previous session's storage are NOT
 * part of this visit — including them re-branded a clean shared/direct/
 * internal navigation as a paid click (LIVE PT-MT1GWHXX). Only click IDs with
 * "url" provenance for this tab session are emitted; the provenance map is
 * embedded so the server and admin UI can verify instead of trusting labels.
 * Stored (stale) click IDs remain available via getAttribution() for
 * conversion-evidence paths — they are excluded from the TOUCH, not deleted.
 */
export function getLastTouch(): FirstTouchSnapshot | null {
  try {
    const data = getAttribution();
    const fresh = (field: ClickIdField, value: string | null): string | null =>
      value && getClickIdProvenance(field) === "url" ? value : null;
    const freshFbclid = fresh("fbclid", data.fbclid);
    return {
      session_id:    data.session_id,
      first_seen_at: data.first_seen_at,
      channel:       buildChannel(),
      fullSource:    buildFullSource(),
      fbclid:        freshFbclid,
      fbc:           freshFbclid ? data.fbc : null,
      fbclid_ts:     freshFbclid ? data.fbclid_ts : null,
      gclid:         fresh("gclid", data.gclid),
      gbraid:        fresh("gbraid", data.gbraid),
      wbraid:        fresh("wbraid", data.wbraid),
      msclkid:       fresh("msclkid", data.msclkid),
      ttclid:        fresh("ttclid", data.ttclid),
      utm_source:    data.utm_source,
      utm_medium:    data.utm_medium,
      utm_campaign:  data.utm_campaign,
      utm_term:      data.utm_term,
      utm_content:   data.utm_content,
      gad_source:    data.gad_source,
      keyword:       data.keyword,
      search_term:   data.search_term,
      matchtype:     data.matchtype,
      network:       data.network,
      device:        data.device,
      placement:     data.placement,
      campaign_id:   data.campaign_id,
      adset_id:      data.adset_id,
      ad_id:         data.ad_id,
      ref:           data.ref,
      landing_url:   data.landing_url,
      referrer:      data.referrer,
      captured_at:   new Date().toISOString(),
      click_provenance: getClickIdProvenanceMap(),
      provenance_version: 1,
    };
  } catch {
    return null;
  }
}

// ── Build attribution_json for backend payloads ───────────────────────────────

/**
 * Returns a clean attribution_json object ready to be stored in the DB.
 * Strips null values to keep the JSON compact.
 * Adds a captured_stage label for debugging.
 *
 * As of analytics-phase-1 the JSON also embeds first_touch + last_touch
 * snapshots so downstream consumers can read both without extra queries.
 */
export function buildAttributionJson(stage: string): Record<string, unknown> {
  const data = getAttribution();

  // Ensure first_touch is captured before we serialize. Either helper may
  // return null/throw under storage failure — guard so we still emit JSON.
  let firstTouch: FirstTouchSnapshot | null = null;
  try { firstTouch = getOrInitFirstTouch(); } catch { firstTouch = null; }
  let lastTouch: FirstTouchSnapshot | null = null;
  try { lastTouch = getLastTouch(); } catch { lastTouch = null; }

  const json: Record<string, unknown> = {
    session_id:    data.session_id,
    first_seen_at: data.first_seen_at,
    captured_stage: stage,
    captured_at:   new Date().toISOString(),
    channel:       buildChannel(),
    ...(firstTouch ? { first_touch: firstTouch } : {}),
    ...(lastTouch  ? { last_touch:  lastTouch  } : {}),
  };

  // Only include non-null fields
  const fields: Array<keyof AttributionData> = [
    "fbclid", "fbc", "fbclid_ts",
    "gclid", "gbraid", "wbraid",
    "msclkid", "ttclid",
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
    "gad_source", "keyword", "search_term", "matchtype", "network", "device",
    "placement", "campaign_id", "adset_id", "ad_id",
    "ref", "landing_url", "referrer",
    "confirmation_id", "coupon_code", "selected_state",
  ];

  // ATTRIBUTION-SOURCE-IMMUTABILITY-001: top-level click IDs feed the flat
  // order columns and the conversion uploader (resolveGclid reads
  // attribution_json). A storage-restored ID is only legitimate here when it
  // matches this browser's own first touch (same acquisition click); a fresh
  // URL-captured ID is always legitimate. Anything else is unproven → omitted.
  const clickIdAllowed = (key: ClickIdField): boolean => {
    if (getClickIdProvenance(key) === "url") return true;
    const ftVal = firstTouch ? (firstTouch[key] as string | null) : null;
    return !!ftVal && ftVal === data[key];
  };

  fields.forEach((key) => {
    if (data[key] === null || data[key] === undefined) return;
    if ((CLICK_ID_FIELDS as readonly string[]).includes(key) && !clickIdAllowed(key as ClickIdField)) {
      return;
    }
    json[key] = data[key];
  });

  // fbc / fbclid_ts are derivatives of fbclid — they ride its gate.
  if (!("fbclid" in json)) {
    delete json.fbc;
    delete json.fbclid_ts;
  }

  debugLog(`buildAttributionJson [${stage}]`, json);

  return json;
}

// ── Build human-readable fullSource label ─────────────────────────────────────

export function buildFullSource(): string {
  const data = getAttribution();
  const { ref, gclid, gbraid, wbraid, fbclid, msclkid, ttclid, utm_source, utm_medium, referrer } = data;

  // ATTRIBUTION-SOURCE-IMMUTABILITY-001: a click ID only proves a paid visit
  // when it was captured from a URL in THIS tab session. Storage-restored IDs
  // must not label a clean return/shared/internal visit as paid.
  const fresh = (field: ClickIdField, value: string | null): boolean =>
    !!value && getClickIdProvenance(field) === "url";

  if (ref) return ref;
  if (fresh("gclid", gclid) || fresh("gbraid", gbraid) || fresh("wbraid", wbraid)) return "Google Ads";
  if (fresh("fbclid", fbclid)) return "Facebook / Instagram Ads";
  if (fresh("msclkid", msclkid)) return "Microsoft Ads";
  if (fresh("ttclid", ttclid)) return "TikTok Ads";
  if (utm_source && utm_medium) return `${utm_source} / ${utm_medium}`;
  if (utm_source) return utm_source;
  if (referrer) {
    const aiHost = detectAiChannelFromReferrer(referrer);
    if (aiHost) return aiHost;
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      if (host.includes("google") || host.includes("bing") || host.includes("yahoo")) return "Google Organic";
      if (host.includes("facebook") || host.includes("instagram")) return "Facebook Organic";
      return `Referral: ${host}`;
    } catch {
      return referrer;
    }
  }
  return "Direct";
}

// ── Build strict channel (canonical attribution) ──────────────────────────────
/**
 * Returns the canonical channel using a LAST-TOUCH priority.
 *
 * Priority:
 *   1. Current URL has utm_source →
 *        facebook/fb/instagram/ig/meta → "facebook_ads"
 *        google                         → "google_ads"
 *        otherwise                      → normalized utm_source (lowercased)
 *   2. Current URL has fbclid     → "facebook_ads"
 *   3. Current URL has gclid      → "google_ads"
 *   4. Stored attribution:
 *        stored fbclid            → "facebook_ads"
 *        stored gclid             → "google_ads"
 *        stored utm_source        → normalized source
 *   5. Referrer host:
 *        google|bing|yahoo        → "organic_search"
 *        facebook|instagram       → "social_organic"
 *   6. otherwise                  → "direct"
 */
export function buildChannel(): string {
  // ── 1–3. Current URL wins (last touch) ────────────────────────────────────
  const currentSearch = typeof window !== "undefined" ? window.location.search : "";
  if (currentSearch) {
    const p = new URLSearchParams(currentSearch);
    const urlUtm    = sanitizeMacroValue(p.get("utm_source"));
    const urlFbclid = p.get("fbclid");
    const urlGclid  = p.get("gclid");
    const urlGbraid = p.get("gbraid");
    const urlWbraid = p.get("wbraid");
    if (urlUtm) {
      const normalized = normalizeSource(urlUtm);
      if (normalized) return normalized;
    }
    if (urlFbclid) return "facebook_ads";
    if (urlGclid)  return "google_ads";
    // gbraid / wbraid are Google Ads click ids (iOS/app + web-to-app). A visit
    // arriving with only gbraid/wbraid (no gclid/utm) is still Google paid.
    if (urlGbraid || urlWbraid) return "google_ads";
  }

  // ── 4. Stored attribution ─────────────────────────────────────────────────
  // ATTRIBUTION-SOURCE-IMMUTABILITY-001: stored click IDs count as a paid
  // channel ONLY when captured from a URL during this tab session ("url"
  // provenance). A storage-restored ID (previous session) must not turn a
  // clean direct/shared/internal visit into a paid channel. Stored UTMs are
  // sessionStorage-scoped (same tab session) so they need no extra gate.
  const data = getAttribution();
  const { gclid, gbraid, wbraid, fbclid, utm_source, referrer } = data;
  const fresh = (field: ClickIdField, value: string | null): boolean =>
    !!value && getClickIdProvenance(field) === "url";

  // gbraid / wbraid are Google Ads click ids too (iOS/app + web-to-app), so
  // they carry the SAME provenance requirement as gclid: a stored one must
  // not turn a clean return/shared visit into a paid channel. The URL branch
  // above still treats a gbraid/wbraid arriving in THIS url as paid.
  if (fresh("fbclid", fbclid)) return "facebook_ads";
  if (fresh("gclid", gclid) || fresh("gbraid", gbraid) || fresh("wbraid", wbraid)) return "google_ads";
  if (utm_source) {
    const normalized = normalizeSource(utm_source);
    if (normalized) return normalized;
  }

  // ── 5. Referrer ──────────────────────────────────────────────────────────
  if (referrer) {
    // AI answer engines (ChatGPT, Claude, Gemini, ...) frequently arrive with
    // ONLY a referrer and no utm/click id. Detect them BEFORE the search-engine
    // host check so e.g. gemini.google.com is not mislabeled "organic_search".
    const aiHost = detectAiChannelFromReferrer(referrer);
    if (aiHost) return aiHost;
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      if (host.includes("google") || host.includes("bing") || host.includes("yahoo")) {
        return "organic_search";
      }
      if (host.includes("facebook") || host.includes("instagram")) {
        return "social_organic";
      }
    } catch {
      // fall through to direct
    }
  }

  // ── 6. Direct ────────────────────────────────────────────────────────────
  return "direct";
}

// ── Build URL query string for link attribution ───────────────────────────────

/**
 * Returns a query string with attribution params for appending to links.
 * Only includes params that have values.
 *
 * ATTRIBUTION-SOURCE-IMMUTABILITY-001: click IDs (gclid/gbraid/wbraid/fbclid/
 * msclkid/ttclid) are deliberately NOT appended. Internal SPA navigation keeps
 * them in session/localStorage, so the URL copy served no in-app purpose —
 * but it put paid-click credentials into the address bar, where a copied /
 * manually-shared link carried them to another person's browser and re-branded
 * their organic visit as a paid click (LIVE PT-MT1GWHXX). Campaign LABELS
 * (UTMs, ValueTrack, ref) stay: they describe a campaign, not a click.
 */
export function buildAttributionQueryString(existingQuery?: string): string {
  const data = getAttribution();
  const merged = new URLSearchParams(existingQuery ?? "");
  let carriesStoredAttribution = false;

  const linkFields: Array<[string, string | null]> = [
    ["utm_source",   data.utm_source],
    ["utm_medium",   data.utm_medium],
    ["utm_campaign", data.utm_campaign],
    ["utm_term",     data.utm_term],
    ["utm_content",  data.utm_content],
    ["gad_source",   data.gad_source],
    ["kw",           data.keyword],
    ["search_term",  data.search_term],
    ["matchtype",    data.matchtype],
    ["adnetwork",    data.network],
    ["addevice",     data.device],
    ["placement",    data.placement],
    ["campaign_id",  data.campaign_id],
    ["adset_id",     data.adset_id],
    ["ad_id",        data.ad_id],
    ["ref",      data.ref],
  ];

  linkFields.forEach(([key, value]) => {
    if (value) carriesStoredAttribution = true;
    if (value && !merged.has(key)) {
      merged.set(key, value);
    }
  });

  // Tell captureFromUrl that these labels came from PawTenant's own stored
  // attribution, not from a new external campaign click. Keep click IDs out of
  // the URL; they remain in session/localStorage with their original
  // provenance.
  if (carriesStoredAttribution) merged.set(INTERNAL_ATTR_PARAM, "1");

  const str = merged.toString();
  return str ? `?${str}` : "";
}

/**
 * Appends attribution params to a path string.
 * Skips external URLs, tel:, mailto:.
 */
export function appendAttribution(path: string): string {
  if (!path || path.startsWith("http") || path.startsWith("tel:") || path.startsWith("mailto:")) {
    return path;
  }
  const [basePath, existingQuery] = path.split("?");
  const qs = buildAttributionQueryString(existingQuery);
  return qs ? `${basePath}${qs}` : basePath;
}

// ── Debug: dump full state ────────────────────────────────────────────────────

/** Call from browser console: window.__dumpAttribution() */
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__dumpAttribution = () => {
    const data = getAttribution();
    console.group("[Attribution] Full State Dump");
    console.table(data);
    console.log("attribution_json (step2_lead):", buildAttributionJson("step2_lead"));
    console.log("fullSource:", buildFullSource());
    console.log("channel:", buildChannel());
    console.groupEnd();
    return data;
  };
}
